import {useCallback, useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import {ETableSort} from 'webapp-lib/Components';
import {useMountEffect} from 'webapp-lib/utils';

import {useVersionInfo} from '_/hooks/useVersionInfo';
import {userState} from '_/state/user';
import {globalSearchParams} from '_/utilities';
import {IFilter} from '_/utilities/filter';

import {useActiveCasusId} from './useActiveCasusId';
import {useQueryParams} from './useQueryParams';

type UseTablePersistenceOptions<
  FILTER extends {[key in string]: any},
  SORT extends ETableSort<any>,
> = {
  key: string;
  enabled?: boolean;
  filters?: FILTER;
  sort?: SORT;
  filtersUpdated?: (filters: FILTER) => void;
  sortUpdated?: (sort: SORT) => void;
  onInitializationFinished?: (filters: FILTER | undefined) => void;
  /** allows to not use casus specific persistence */
  ignoreCasusContext?: boolean;
  queryParamFilterOverride?: {[key in string]: string};
};

const storageKey = 'lexian.tablePersistence';

type PersistedState<
  FILTER extends {[key in string]: any},
  SORT extends ETableSort<any>,
> = {
  version?: string;
  data: {
    [key in string]: {
      filters: FILTER;
      sort: SORT;
    };
  };
};

const parseState = <
  FILTER extends {[key in string]: any},
  SORT extends ETableSort<any>,
>(
  json: string | null | undefined
): PersistedState<FILTER, SORT> | undefined => {
  if (json === null || json === undefined) {
    return undefined;
  }
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
};

export const useTablePersistence = <
  FILTER extends {[key in string]: any},
  SORT extends ETableSort<any>,
>({
  key,
  enabled = true,
  sort,
  filters,
  sortUpdated,
  filtersUpdated,
  onInitializationFinished,
  ignoreCasusContext,
  queryParamFilterOverride,
}: UseTablePersistenceOptions<FILTER, SORT>): boolean => {
  const user = useRecoilValue(userState);
  const {frontend: frontendVersion} = useVersionInfo();
  const activeCasusId = useActiveCasusId();
  const {queryParams, deleteQueryParam} = useQueryParams();

  const [initialized, setInitialized] = useState(false);
  const userKey =
    activeCasusId && !ignoreCasusContext
      ? `${user.userId}.${activeCasusId}.${key}`
      : `${user.userId}.${key}`;

  const loadPersistedState = useCallback(():
    | PersistedState<FILTER, SORT>
    | undefined => {
    const state = parseState<FILTER, SORT>(
      window.localStorage.getItem(storageKey)
    );

    if (!frontendVersion || !enabled) {
      return undefined;
    }

    if (state?.version !== frontendVersion) {
      return {
        version: frontendVersion,
        data: {},
      };
    } else {
      return state;
    }
  }, [enabled, frontendVersion]);

  const filtersToStore = Object.fromEntries(
    Object.entries(filters ?? {}).map(([fieldName, fieldValue]) => {
      if (isFilter(fieldValue)) {
        return [fieldName, fieldValue.item];
      } else {
        return [];
      }
    })
  );

  useMountEffect(() => {
    const persistedState = loadPersistedState();
    if (!persistedState || !enabled) {
      setInitialized(true);
      return;
    }

    const persistedUserState = persistedState.data[userKey];

    if (persistedUserState?.sort) {
      try {
        sortUpdated?.(persistedUserState.sort);
      } catch {
        // ignore errors caused by changed sort format
      }
    }
    if (queryParams.get(globalSearchParams.clearFilters) === 'true') {
      deleteQueryParam(globalSearchParams.clearFilters);
    } else if (persistedUserState?.filters) {
      Object.entries(filters ?? {}).forEach(([fieldName, fieldValue]) => {
        let value = persistedUserState.filters[fieldName];
        value =
          Array.isArray(fieldValue.item) && !Array.isArray(value)
            ? [value]
            : value;
        if (isFilter(fieldValue)) {
          const filterQueryName = queryParamFilterOverride?.[fieldName];
          let possibleFilterValueOverride: string | string[] | null =
            filterQueryName ? queryParams.get(filterQueryName) : null;

          if (Array.isArray(fieldValue.item)) {
            possibleFilterValueOverride =
              possibleFilterValueOverride?.split(',') ?? null;
          }

          // no point in setting nullish-values
          if (
            (possibleFilterValueOverride !== null &&
              possibleFilterValueOverride !== undefined) ||
            (value !== undefined && value !== null)
          ) {
            try {
              fieldValue.setElements(possibleFilterValueOverride ?? value);
              if (possibleFilterValueOverride && filterQueryName) {
                deleteQueryParam(filterQueryName);
              }
            } catch {
              // ignore errors caused by changed filter format
            }
          }
        }
      });
      if (filters !== undefined) {
        filtersUpdated?.(filters);
      }
    }
    setInitialized(true);
    onInitializationFinished?.(filters);
  });

  useEffect(() => {
    if (!initialized || !enabled) {
      return;
    }

    const persistedState = loadPersistedState();

    if (!persistedState) {
      return;
    }

    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        ...persistedState,
        data: {
          ...persistedState?.data,
          [userKey]: {
            filters: filtersToStore,
            sort,
          },
        },
      })
    );
  }, [loadPersistedState, userKey, initialized, filtersToStore, sort, enabled]);

  if (!enabled) {
    return true;
  }
  return initialized;
};

const isFilter = (f: any): f is IFilter<any> => 'item' in f;
